import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  isTimerInPreCountdown,
  normaliseSoundCues,
  parseTimerState,
  type TimerState,
} from '../../utils/timerState';
import useGameStore from '../../stores/gameStore';
import { generateAudioUrl } from '../../utils/audioUtils';
import { scheduleTimerSoundCues } from '../../utils/timerSoundScheduler';
import { useTimerAudioUnlock } from '../../hooks/useTimerAudioUnlock';

export interface TimerAudioProps {
  timerVar: string;
  startedAt?: string | undefined;
  stoppedAt?: string | undefined;
  maxSeconds?: string | undefined;
  targetAt?: string | undefined;
  /** JSON sound cue list from displayTimerAudio markup. */
  soundCuesJson?: string | undefined;
  armedAt?: string | undefined;
  preCountdownSeconds?: string | undefined;
  preCountdownAudioId?: string | undefined;
}

function parseOptionalMs(value: string | undefined): number | null {
  if (value === undefined || value === null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function parseOptionalSeconds(value: string | undefined): number | null {
  if (value === undefined || value === null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

function playAudioClip(audioId: string): void {
  const audio = new Audio(generateAudioUrl(audioId));
  void audio.play().catch(() => {
    /* autoplay may still be blocked without prior gesture */
  });
}

/**
 * Invisible scheduler that plays timer sound cues and pre-countdown audio.
 */
const TimerAudio: React.FC<TimerAudioProps> = ({
  timerVar,
  startedAt: startedAtProp,
  stoppedAt: stoppedAtProp,
  maxSeconds: maxSecondsProp,
  targetAt: targetAtProp,
  soundCuesJson,
  armedAt: armedAtProp,
  preCountdownSeconds: preCountdownSecondsProp,
  preCountdownAudioId: preCountdownAudioIdProp,
}) => {
  useTimerAudioUnlock();

  const soundCuesFromProps = useMemo(() => {
    if (!soundCuesJson) return null;
    try {
      return normaliseSoundCues(JSON.parse(soundCuesJson));
    } catch {
      return [];
    }
  }, [soundCuesJson]);

  const rawValue = useGameStore(
    useCallback((state) => state.userVariableValues[timerVar]?.value ?? '', [timerVar])
  );

  const stateFromStore = useMemo(
    () => parseTimerState(rawValue === '' ? undefined : String(rawValue)),
    [rawValue]
  );

  const startedAtMs = parseOptionalMs(startedAtProp);
  const stoppedAtMs = parseOptionalMs(stoppedAtProp);
  const maxSecondsFromProp = parseOptionalSeconds(maxSecondsProp);
  const targetAtMs = parseOptionalMs(targetAtProp);
  const armedAtMs = parseOptionalMs(armedAtProp);
  const preCountdownSecondsFromProp = parseOptionalSeconds(preCountdownSecondsProp);

  const useProps = startedAtMs !== null || targetAtMs !== null || armedAtMs !== null;
  const state: TimerState = useMemo(() => {
    if (useProps) {
      const maxSec = maxSecondsFromProp ?? stateFromStore.maxSeconds;
      const targetAt = targetAtMs ?? stateFromStore.targetAt;
      const mode =
        targetAt !== null
          ? 'deadline'
          : maxSec !== null
            ? 'max'
            : stateFromStore.mode;
      return {
        ...stateFromStore,
        mode,
        startedAt: startedAtMs ?? stateFromStore.startedAt,
        stoppedAt: stoppedAtMs,
        maxSeconds: maxSec,
        targetAt,
        armedAt: armedAtMs ?? stateFromStore.armedAt,
        preCountdownSeconds:
          preCountdownSecondsFromProp ?? stateFromStore.preCountdownSeconds,
        preCountdownAudioId:
          preCountdownAudioIdProp ?? stateFromStore.preCountdownAudioId,
        soundCues: soundCuesFromProps ?? stateFromStore.soundCues,
      };
    }
    return {
      ...stateFromStore,
      soundCues: soundCuesFromProps ?? stateFromStore.soundCues,
    };
  }, [
    useProps,
    startedAtMs,
    stoppedAtMs,
    maxSecondsFromProp,
    targetAtMs,
    armedAtMs,
    preCountdownSecondsFromProp,
    preCountdownAudioIdProp,
    stateFromStore,
    soundCuesFromProps,
  ]);

  const isRunning = state.startedAt !== null && state.stoppedAt === null;
  const firedKeysRef = useRef<Set<string>>(new Set());
  const preCountdownPlayedRef = useRef(false);
  const runIdRef = useRef(0);

  useEffect(() => {
    if (!isRunning) {
      firedKeysRef.current.clear();
      preCountdownPlayedRef.current = false;
    }
  }, [isRunning, state.startedAt]);

  useEffect(() => {
    if (!isRunning || !state.preCountdownAudioId || state.armedAt === null) return;

    const armedAt = state.armedAt;
    const audioId = state.preCountdownAudioId;

    const play = () => {
      if (preCountdownPlayedRef.current) return;
      if (state.stoppedAt !== null) return;
      preCountdownPlayedRef.current = true;
      playAudioClip(audioId);
    };

    const now = Date.now();
    if (now < armedAt) {
      const timeoutId = setTimeout(play, armedAt - now);
      return () => clearTimeout(timeoutId);
    }
    if (isTimerInPreCountdown(state, now)) {
      play();
    }
    return undefined;
  }, [isRunning, state]);

  useEffect(() => {
    if (!isRunning || state.soundCues.length === 0) return;

    const runId = ++runIdRef.current;
    const schedule = scheduleTimerSoundCues(
      state,
      state.soundCues,
      firedKeysRef.current,
      (cue) => {
        if (runIdRef.current !== runId) return;
        playAudioClip(cue.audioId);
      }
    );

    return () => schedule.clear();
  }, [isRunning, state]);

  return null;
};

export default TimerAudio;
